import { useState } from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { BookOpen, CheckCircle2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { cn } from "@/lib/utils"

const textareaClass =
  "flex min-h-28 w-full rounded border border-input bg-background px-3 py-2 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive"

/**
 * The driver's "I'm stuck" button, in the top bar of every page they can reach.
 * Sends the question to the office and keeps a copy in the admin inbox, so a
 * driver never has to work out who to call. The Driver Guide sits in the same
 * box: some questions are answered faster by reading than by waiting.
 */
export function DriverHelpButton({
  createPath,
  guidePath,
  className,
}: {
  createPath: string
  guidePath?: string | null
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)
  const page = usePage()
  const form = useForm({ message: "", asked_from: page.url })

  function onOpenChange(next: boolean) {
    setOpen(next)
    // Reopening starts fresh — a stale "thanks" or a stale error would be
    // answering a question the driver hasn't asked yet.
    if (!next) {
      setSent(false)
      form.reset()
      form.clearErrors()
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ ...data, asked_from: page.url }))
    form.post(createPath, {
      preserveScroll: true,
      onSuccess: () => {
        setSent(true)
        form.reset("message")
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {/* Spelled out, not an icon: a life preserver reads as "rescue", and a
            driver who needs this is already unsure of themselves. */}
        <Button type="button" variant="outline" size="sm" className={className}>
          Ask for help
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        {sent ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
                Message sent
              </DialogTitle>
              <DialogDescription>
                The office has your message and will get back to you. You can close this window.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Need a hand?</DialogTitle>
              <DialogDescription>
                Tell us what you're stuck on and someone from the office will get back to you.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-4">
              <label htmlFor="help-message" className="text-sm font-medium">
                What do you need help with?
              </label>
              <textarea
                id="help-message"
                required
                maxLength={2000}
                value={form.data.message}
                onChange={(event) => form.setData("message", event.target.value)}
                aria-invalid={form.errors.message ? true : undefined}
                className={textareaClass}
                placeholder="I can't upload my medical card."
              />
              <FieldError message={form.errors.message} />

              {guidePath && (
                <Link
                  href={guidePath}
                  onClick={() => onOpenChange(false)}
                  className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
                >
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  Read the Driver Guide first
                </Link>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Sending…" : "Send to the office"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Shared chrome for the square icon controls in the top bar. */
export function topBarIconClass(active: boolean) {
  return cn(
    "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground",
  )
}
